import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor, { DiffEditor, loader } from '@monaco-editor/react';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';
import StaleContentBanner from '../../components/StaleContentBanner';
import {
    FaPlus,
    FaCode,
    FaSpinner,
    FaTimes,
    FaExclamationTriangle,
    FaTerminal,
    FaFileAlt,
    FaFilter,
    FaSortAmountDown,
    FaSortAmountUp,
    FaUnlink,
    FaSave,
    FaEdit,
    FaEye,
    FaHistory,
    FaExchangeAlt,
    FaUndo,
    FaChevronDown,
    FaChevronRight,
    FaColumns,
    FaLink,
    FaFileImport,
    FaTrash,
    FaTag,
    FaGripVertical,
    FaArrowRight,
    FaArrowLeft,
    FaPencilAlt,
    FaBook,
    FaExternalLinkAlt
} from 'react-icons/fa';

/**
 * ONE unsaved-changes prompt on this page (D3). The banner's Reload puts up the
 * SAME confirm the selection guard already uses - reuse it, do not write a
 * second prompt.
 */
const UNSAVED_CHANGES_PROMPT = 'You have unsaved changes. Are you sure you want to leave?';

// Load Monaco from our own origin, not cdn.jsdelivr.net. @monaco-editor/loader
// defaults to fetching it from that CDN at runtime, which is a third-party script
// on an admin page and incompatible with a CSP of script-src 'self'. The assets
// are copied into public/monaco by scripts/copy-monaco.js on every start/build.
//
// PUBLIC_URL, not an absolute '/monaco/vs': prod serves from the root but preview
// serves from https://preview.themigrationexperts.com/{branch}/, where an absolute
// path would 404 and leave the editor blank.
//
// Module scope on purpose - this must run before any <Editor> mounts.
loader.config({ paths: { vs: `${process.env.PUBLIC_URL}/monaco/vs` } });

const STORAGE_KEY = 'scriptRepositoryState';

function ScriptRepository() {
    const { scriptId: urlScriptId, versionNumber: urlVersionNumber } = useParams();
    const navigate = useNavigate();

    // Load initial state from localStorage
    const getInitialState = () => {
        const defaults = {
            searchTerm: '',
            filters: {
                scriptType: new Set(),
                author: new Set(),
                tags: new Set()
            },
            sortField: 'updatedAt',
            sortDir: 'desc',
            showUnlinked: false
        };

        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Merge with defaults to ensure all properties exist
                const result = { ...defaults, ...parsed };

                // Ensure filters object has all required keys and convert arrays to Sets
                result.filters = {
                    scriptType: new Set(),
                    author: new Set(),
                    tags: new Set()
                };
                if (parsed.filters) {
                    Object.keys(parsed.filters).forEach(key => {
                        if (result.filters.hasOwnProperty(key)) {
                            result.filters[key] = Array.isArray(parsed.filters[key])
                                ? new Set(parsed.filters[key])
                                : (parsed.filters[key] instanceof Set ? parsed.filters[key] : new Set());
                        }
                    });
                }
                return result;
            }
        } catch (err) {
            console.error('Failed to load script repository state:', err);
        }
        return defaults;
    };

    // Default filter structure - used throughout component
    const createDefaultFilters = () => ({
        scriptType: new Set(),
        author: new Set(),
        tags: new Set()
    });

    // Load initial state from localStorage with guaranteed filter structure
    const initialState = useMemo(() => {
        const state = getInitialState();
        // Guarantee filters object exists and has correct structure
        if (!state.filters || typeof state.filters !== 'object') {
            state.filters = createDefaultFilters();
        }
        return state;
    }, []);

    // Scripts list state
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selected script state
    const [selectedScript, setSelectedScript] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [loadingScript, setLoadingScript] = useState(false);

    // Editor state
    const [editorContent, setEditorContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [loadingContent, setLoadingContent] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const editorRef = useRef(null);

    // Save modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [versionNotes, setVersionNotes] = useState('');
    const [saving, setSaving] = useState(false);

    // Version history state
    const [versions, setVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [versionHistoryExpanded, setVersionHistoryExpanded] = useState(() => {
        return localStorage.getItem('scriptVersionHistoryExpanded') === 'true';
    });

    // Diff viewer state
    const [showDiffViewer, setShowDiffViewer] = useState(false);
    const [diffSideBySide, setDiffSideBySide] = useState(true);
    const [compareVersions, setCompareVersions] = useState({ v1: null, v2: null });
    const [compareContent, setCompareContent] = useState({ original: '', modified: '' });
    const [loadingCompare, setLoadingCompare] = useState(false);

    // Rollback state
    const [rollingBack, setRollingBack] = useState(false);

    // Task links state
    const [scriptLinks, setScriptLinks] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [taskLinksExpanded, setTaskLinksExpanded] = useState(() => {
        return localStorage.getItem('scriptTaskLinksExpanded') === 'true';
    });
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [availableTasks, setAvailableTasks] = useState([]);
    const [loadingAvailableTasks, setLoadingAvailableTasks] = useState(false);
    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const [taskTypeFilter, setTaskTypeFilter] = useState('all');
    const [linking, setLinking] = useState(null);
    const [unlinking, setUnlinking] = useState(null);
    const [draggedTask, setDraggedTask] = useState(null);
    const [dragOverPanel, setDragOverPanel] = useState(null);

    // "Used by TME Wiki" (TWK-24, §9.6) - READ ONLY. Which internal wiki pages
    // reference this script. A page links itself; nothing is linked from here.
    const [wikiPages, setWikiPages] = useState([]);
    const [loadingWikiPages, setLoadingWikiPages] = useState(false);
    const [wikiPagesExpanded, setWikiPagesExpanded] = useState(() => {
        return localStorage.getItem('scriptWikiPagesExpanded') === 'true';
    });

    // Import modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importData, setImportData] = useState(null);
    const [importFormData, setImportFormData] = useState({ description: '', tags: [] });
    const [importing, setImporting] = useState(false);
    const [newTag, setNewTag] = useState('');
    const fileInputRef = useRef(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmName, setDeleteConfirmName] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [scriptToDelete, setScriptToDelete] = useState(null);
    const [deleteLinkedTasks, setDeleteLinkedTasks] = useState([]);
    const [loadingDeleteLinks, setLoadingDeleteLinks] = useState(false);

    // Rename state
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameError, setRenameError] = useState('');
    const [savingRename, setSavingRename] = useState(false);

    // Create script modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createFormData, setCreateFormData] = useState({ name: '', description: '', scriptType: 'powershell', tags: [] });
    const [creating, setCreating] = useState(false);
    const [createNewTag, setCreateNewTag] = useState('');

    // Search state
    const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);

    // Filter state - ALWAYS create fresh Set objects to guarantee structure
    const [filtersState, setFiltersStateRaw] = useState(() => {
        const f = initialState.filters;
        // Always return a new object with guaranteed Set properties
        return {
            scriptType: (f && f.scriptType instanceof Set) ? new Set(f.scriptType) : new Set(),
            author: (f && f.author instanceof Set) ? new Set(f.author) : new Set(),
            tags: (f && f.tags instanceof Set) ? new Set(f.tags) : new Set()
        };
    });

    // SAFE SETTER: Wrap the state setter to REJECT undefined/null values
    const setFiltersState = useCallback((valueOrUpdater) => {
        setFiltersStateRaw(prev => {
            // Handle both direct values and functional updates
            const newValue = typeof valueOrUpdater === 'function'
                ? valueOrUpdater(prev)
                : valueOrUpdater;
            // NEVER allow undefined or null - always fall back to default
            if (!newValue || typeof newValue !== 'object') {
                return createDefaultFilters();
            }
            return newValue;
        });
    }, []);

    // SAFETY WRAPPER: Guarantee filters is NEVER undefined at any access point
    // This creates a derived value that is ALWAYS a valid filters object
    const filters = filtersState && typeof filtersState === 'object' ? filtersState : createDefaultFilters();
    const setFilters = setFiltersState;
    const [showUnlinked, setShowUnlinked] = useState(initialState.showUnlinked);
    const [openFilter, setOpenFilter] = useState(null);

    // Sort state
    const [sortField, setSortField] = useState(initialState.sortField);
    const [sortDir, setSortDir] = useState(initialState.sortDir);

    // Persist state to localStorage
    useEffect(() => {
        try {
            // LOCAL safety - use inline default if filters is somehow undefined
            const safeFilters = filters || { scriptType: new Set(), author: new Set(), tags: new Set() };
            // Convert Sets to arrays for JSON serialisation (with defensive checks)
            const filtersForStorage = {
                scriptType: safeFilters.scriptType ? [...safeFilters.scriptType] : [],
                author: safeFilters.author ? [...safeFilters.author] : [],
                tags: safeFilters.tags ? [...safeFilters.tags] : []
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                searchTerm,
                filters: filtersForStorage,
                sortField,
                sortDir,
                showUnlinked
            }));
        } catch (err) {
            console.error('Failed to save script repository state:', err);
        }
    }, [searchTerm, filters, sortField, sortDir, showUnlinked]);

    // Persist version history expanded state
    useEffect(() => {
        localStorage.setItem('scriptVersionHistoryExpanded', versionHistoryExpanded.toString());
    }, [versionHistoryExpanded]);

    // Persist task links expanded state
    useEffect(() => {
        localStorage.setItem('scriptTaskLinksExpanded', taskLinksExpanded.toString());
    }, [taskLinksExpanded]);

    useEffect(() => {
        localStorage.setItem('scriptWikiPagesExpanded', wikiPagesExpanded.toString());
    }, [wikiPagesExpanded]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openFilter && !e.target.closest('.filter-dropdown')) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilter]);

    // Fetch scripts list
    // ⚠️ SILENT MODE (LIV, D1) on every loader the message path calls: no
    // loading flag, no error banner, and - the half that matters - the data on
    // screen KEPT on failure. Three of the loaders below blank their list to []
    // when a request fails, which is right on mount and quite wrong for a
    // refresh nobody asked for.
    const fetchScripts = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setError(null);
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts`);
            if (response.ok) {
                const data = await response.json();
                if (!data.scripts) {
                    throw new Error('API response missing scripts array');
                }
                setScripts(data.scripts);
            } else if (!silent) {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to fetch scripts');
            } else {
                console.warn('Background script refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch scripts:', err);
            if (!silent) setError('Failed to fetch scripts');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Fetch single script details
    // ⚠️ SILENT MODE (LIV, D1) matters MORE here than on any other loader on the
    // page: `loadingScript` swaps the entire right panel for a spinner, so a
    // non-silent call from the live path unmounted Monaco - cursor, scroll and
    // undo history gone - a second after every save, when the echo arrived; and
    // every failure path nulls `selectedScript`, which deselects the script the
    // operator is looking at. Silent: no flag, the selection kept on failure.
    const fetchScript = useCallback(async (scriptId, { silent = false } = {}) => {
        if (!scriptId) return;
        if (!silent) setLoadingScript(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}`);
            if (response.ok) {
                const data = await response.json();
                // Validate that the response has required fields
                if (data && data.scriptId && data.scriptType) {
                    setSelectedScript(data);
                } else if (!silent) {
                    console.error('Invalid script data received:', data);
                    setError('Invalid script data received');
                    setSelectedScript(null);
                } else {
                    console.warn('Background script refresh returned an invalid script - keeping the one on screen');
                }
            } else if (!silent) {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to fetch script');
                setSelectedScript(null);
            } else {
                console.warn('Background script refresh failed - keeping the script on screen');
            }
        } catch (err) {
            console.error('Failed to fetch script:', err);
            if (!silent) {
                setError('Failed to fetch script');
                setSelectedScript(null);
            }
        } finally {
            if (!silent) setLoadingScript(false);
        }
    }, []);

    // Fetch script content
    const fetchScriptContent = useCallback(async (scriptId, versionNumber, { silent = false } = {}) => {
        if (!scriptId) return;
        if (!silent) setLoadingContent(true);
        try {
            const url = versionNumber
                ? `${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/content/${versionNumber}`
                : `${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/content`;
            const response = await apiFetch(url);
            if (response.ok) {
                const data = await response.json();
                const content = data.content ? data.content : '';
                setEditorContent(content);
                setOriginalContent(content);
                setHasUnsavedChanges(false);
                // Set editing mode only for current version (not viewing historical)
                setIsEditing(!versionNumber);
            } else {
                const data = await response.json();
                // Handle no versions case gracefully
                if (data.message === 'No versions exist') {
                    setEditorContent('');
                    setOriginalContent('');
                    setHasUnsavedChanges(false);
                    setIsEditing(true);
                } else if (!silent) {
                    setError(data.error ? data.error : 'Failed to fetch content');
                }
            }
        } catch (err) {
            console.error('Failed to fetch script content:', err);
            if (!silent) setError('Failed to fetch script content');
        } finally {
            if (!silent) setLoadingContent(false);
        }
    }, []);

    // Fetch version history for a script
    const fetchVersions = useCallback(async (scriptId, { silent = false } = {}) => {
        if (!scriptId) return;
        if (!silent) setLoadingVersions(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/versions`);
            if (response.ok) {
                const data = await response.json();
                setVersions(data.versions ? data.versions : []);
            } else {
                console.error('Failed to fetch versions');
                if (!silent) setVersions([]);
            }
        } catch (err) {
            console.error('Failed to fetch versions:', err);
            if (!silent) setVersions([]);
        } finally {
            if (!silent) setLoadingVersions(false);
        }
    }, []);

    // Fetch content for a specific version (for comparison)
    const fetchVersionContentForCompare = useCallback(async (scriptId, versionNumber) => {
        const response = await apiFetch(
            `${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/content/${versionNumber}`
        );
        if (response.ok) {
            const data = await response.json();
            return data.content ? data.content : '';
        }
        throw new Error('Failed to fetch version content');
    }, []);

    // Fetch script links and enrich with task titles
    const fetchScriptLinks = useCallback(async (scriptId, { silent = false } = {}) => {
        if (!scriptId) return;
        if (!silent) setLoadingLinks(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/${scriptId}/links`);
            if (response.ok) {
                const data = await response.json();
                // v2: backend returns taskName, taskKeyDisplay, and taskType with each link
                setScriptLinks(data.links ? data.links : []);
            } else {
                console.error('Failed to fetch script links');
                if (!silent) setScriptLinks([]);
            }
        } catch (err) {
            console.error('Failed to fetch script links:', err);
            if (!silent) setScriptLinks([]);
        } finally {
            if (!silent) setLoadingLinks(false);
        }
    }, []);

    /**
     * Which TME Wiki pages reference this script (TWK-24, §9.6).
     *
     * ⚠️ THE FRONTEND CALLS THE TME WIKI ROUTE DIRECTLY, with the same admin
     * token. `AdminScriptsFunction` is granted NOTHING on the internal wiki's
     * tables (§13.2) - one feature's Lambda reaching into another's tables is
     * the shape D18 and D23 both refused, and this is the one place the Script
     * Repository reads TME Wiki data at all.
     *
     * Read-only: a page links itself to a script, from the page. There is no
     * link control here, so no reverse write path exists to grant.
     */
    const fetchWikiPagesForScript = useCallback(async (scriptId, { silent = false } = {}) => {
        if (!scriptId) return;
        if (!silent) setLoadingWikiPages(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/tme-wiki/script-pages/${scriptId}`
            );
            if (response.ok) {
                const data = await response.json();
                // Each row mirrors the SOURCE page's title (H1, R23), so this
                // list names pages without a second read.
                setWikiPages(data.pages ? data.pages : []);
            } else {
                console.error('Failed to fetch TME wiki pages for script');
                if (!silent) setWikiPages([]);
            }
        } catch (err) {
            console.error('Failed to fetch TME wiki pages for script:', err);
            if (!silent) setWikiPages([]);
        } finally {
            if (!silent) setLoadingWikiPages(false);
        }
    }, []);

    const openWikiPage = useCallback((link) => {
        // The base path the rest of the portal computes: the router is mounted
        // under PUBLIC_URL, which on preview is `/gantt`.
        const pathname = window.location.pathname;
        const adminIndex = pathname.indexOf('/admin/');
        const basePath = adminIndex > 0 ? pathname.substring(0, adminIndex) : '';
        window.open(`${basePath}/admin/tme-wiki/wikis/${link.wikiId}/pages/${link.pageId}`, '_blank');
    }, []);

    // Fetch templates for link modal
    // Fetch unique task names across all templates
    const fetchAvailableTasks = useCallback(async () => {
        setLoadingAvailableTasks(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/scripts/available-tasks`);
            if (response.ok) {
                const data = await response.json();
                setAvailableTasks(data.tasks ? data.tasks : []);
            } else {
                console.error('Failed to fetch available tasks');
                setAvailableTasks([]);
            }
        } catch (err) {
            console.error('Failed to fetch available tasks:', err);
            setAvailableTasks([]);
        } finally {
            setLoadingAvailableTasks(false);
        }
    }, []);

    // Get display text for a task link (v2 uses task name)
    const getTaskLinkDisplay = useCallback((link) => {
        // v2: use taskName or taskKeyDisplay
        if (link.taskName) {
            return link.taskName;
        }
        if (link.taskKeyDisplay) {
            return link.taskKeyDisplay;
        }
        // Fallback for old-style links
        return link.taskKey;
    }, []);

    // Get task type label for display
    const getTaskTypeLabel = useCallback((taskType) => {
        if (taskType === 'maintask') return 'Main Task';
        if (taskType === 'subtask') return 'Subtask';
        return '';
    }, []);

    // Link script to a task name (v2)
    const handleLinkToTask = useCallback(async (taskName) => {
        if (!selectedScript || !taskName) return;

        setLinking(taskName);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${selectedScript.scriptId}/links`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ taskName })
                }
            );
            if (response.ok) {
                // Refresh links and scripts list (to update linkCount)
                fetchScriptLinks(selectedScript.scriptId);
                fetchScripts();
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to link script to task');
            }
        } catch (err) {
            console.error('Failed to link script to task:', err);
            setError('Failed to link script to task');
        } finally {
            setLinking(null);
        }
    }, [selectedScript, fetchScriptLinks, fetchScripts]);

    // Unlink script from a task name (v2)
    const handleUnlinkFromTask = useCallback(async (taskName) => {
        if (!selectedScript || !taskName) return;

        const confirmUnlink = window.confirm(`Unlink from "${taskName}"?`);
        if (!confirmUnlink) return;

        setUnlinking(taskName);
        try {
            const encodedTaskName = encodeURIComponent(taskName);
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${selectedScript.scriptId}/links/${encodedTaskName}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                // Refresh links and scripts list (to update linkCount)
                fetchScriptLinks(selectedScript.scriptId);
                fetchScripts();
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to unlink script from task');
            }
        } catch (err) {
            console.error('Failed to unlink script from task:', err);
            setError('Failed to unlink script from task');
        } finally {
            setUnlinking(null);
        }
    }, [selectedScript, fetchScriptLinks, fetchScripts]);

    // Open link modal and fetch available tasks
    const handleOpenLinkModal = useCallback(() => {
        setShowLinkModal(true);
        setTaskSearchTerm('');
        setTaskTypeFilter('all');
        fetchAvailableTasks();
    }, [fetchAvailableTasks]);

    // Drag-and-drop handlers for transfer list
    const handleDragStart = useCallback((e, task, source) => {
        setDraggedTask({ ...task, source });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task.taskName);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedTask(null);
        setDragOverPanel(null);
    }, []);

    const handleDragOver = useCallback((e, panel) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverPanel(panel);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOverPanel(null);
    }, []);

    const handleDropOnLinked = useCallback(async (e) => {
        e.preventDefault();
        setDragOverPanel(null);
        if (!draggedTask || draggedTask.source === 'linked') return;
        await handleLinkToTask(draggedTask.taskName);
        setDraggedTask(null);
    }, [draggedTask, handleLinkToTask]);

    const handleDropOnAvailable = useCallback(async (e) => {
        e.preventDefault();
        setDragOverPanel(null);
        if (!draggedTask || draggedTask.source === 'available') return;
        await handleUnlinkFromTask(draggedTask.taskName);
        setDraggedTask(null);
    }, [draggedTask, handleUnlinkFromTask]);

    // Get list of available (not yet linked) tasks
    const filteredAvailableTasks = useMemo(() => {
        const linkedNames = new Set(scriptLinks.map(l =>
            (l.taskName ? l.taskName : (l.taskKeyDisplay ? l.taskKeyDisplay : l.taskKey)).toLowerCase()
        ));

        return availableTasks.filter(task => {
            // Filter out already linked
            if (linkedNames.has(task.taskName.toLowerCase())) return false;
            // Filter by type
            if (taskTypeFilter !== 'all' && task.taskType !== taskTypeFilter) return false;
            // Filter by search term
            if (taskSearchTerm && !task.taskName.toLowerCase().includes(taskSearchTerm.toLowerCase())) return false;
            return true;
        });
    }, [availableTasks, scriptLinks, taskTypeFilter, taskSearchTerm]);

    // Get list of linked tasks (for right panel of transfer list)
    const linkedTasksList = useMemo(() => {
        return scriptLinks.map(link => {
            const taskName = link.taskName ? link.taskName : (link.taskKeyDisplay ? link.taskKeyDisplay : link.taskKey);
            // Find matching task from availableTasks to get type info
            const taskInfo = availableTasks.find(t => t.taskName.toLowerCase() === taskName.toLowerCase());
            return {
                taskName,
                taskType: link.taskType ? link.taskType : (taskInfo ? taskInfo.taskType : 'unknown'),
                count: taskInfo ? taskInfo.count : 0
            };
        });
    }, [scriptLinks, availableTasks]);

    // Handle comparing two versions
    const handleCompareVersions = useCallback(async (v1, v2) => {
        if (!selectedScript) return;
        setLoadingCompare(true);
        setShowDiffViewer(true);
        setCompareVersions({ v1, v2 });
        try {
            const [content1, content2] = await Promise.all([
                fetchVersionContentForCompare(selectedScript.scriptId, v1),
                fetchVersionContentForCompare(selectedScript.scriptId, v2)
            ]);
            setCompareContent({ original: content1, modified: content2 });
        } catch (err) {
            console.error('Failed to load versions for comparison:', err);
            setError('Failed to load versions for comparison');
            setShowDiffViewer(false);
        } finally {
            setLoadingCompare(false);
        }
    }, [selectedScript, fetchVersionContentForCompare]);

    // Handle rollback to a previous version
    const handleRollback = useCallback(async (versionNumber) => {
        if (!selectedScript) return;
        const confirmRollback = window.confirm(
            `Are you sure you want to rollback to version ${versionNumber}? This will create a new version with the content from version ${versionNumber}.`
        );
        if (!confirmRollback) return;

        setRollingBack(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${selectedScript.scriptId}/rollback/${versionNumber}`,
                { method: 'POST' }
            );
            if (response.ok) {
                // Refresh script, versions, and content
                fetchScript(selectedScript.scriptId);
                fetchVersions(selectedScript.scriptId);
                fetchScriptContent(selectedScript.scriptId, null);
                fetchScripts();
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to rollback');
            }
        } catch (err) {
            console.error('Failed to rollback:', err);
            setError('Failed to rollback');
        } finally {
            setRollingBack(false);
        }
    }, [selectedScript, fetchScript, fetchVersions, fetchScriptContent, fetchScripts]);

    // Close diff viewer
    const handleCloseDiffViewer = useCallback(() => {
        setShowDiffViewer(false);
        setCompareVersions({ v1: null, v2: null });
        setCompareContent({ original: '', modified: '' });
    }, []);

    // Handle file selection for import
    const handleImportFileChange = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext !== 'ps1' && ext !== 'sh') {
            setError('Only .ps1 (PowerShell) and .sh (BASH) files are supported');
            return;
        }

        try {
            const content = await file.text();
            setImportFile(file);
            setImportData({
                name: file.name.replace(/\.(ps1|sh)$/i, ''),
                scriptType: ext === 'ps1' ? 'powershell' : 'bash',
                content,
                filename: file.name
            });
            setImportFormData({ description: '', tags: [] });
            setNewTag('');
        } catch (err) {
            console.error('Failed to read file:', err);
            setError('Failed to read file');
        }
    }, []);

    // Open import modal
    const handleOpenImportModal = useCallback(() => {
        setShowImportModal(true);
        setImportFile(null);
        setImportData(null);
        setImportFormData({ description: '', tags: [] });
        setNewTag('');
    }, []);

    // Close import modal
    const handleCloseImportModal = useCallback(() => {
        setShowImportModal(false);
        setImportFile(null);
        setImportData(null);
        setImportFormData({ description: '', tags: [] });
        setNewTag('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    // Handle adding a tag in import modal
    const handleAddImportTag = useCallback(() => {
        const tag = newTag.trim().toLowerCase();
        if (tag && !importFormData.tags.includes(tag)) {
            setImportFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
            setNewTag('');
        }
    }, [newTag, importFormData.tags]);

    // Handle removing a tag in import modal
    const handleRemoveImportTag = useCallback((tagToRemove) => {
        setImportFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t !== tagToRemove)
        }));
    }, []);

    // Handle import submission
    const handleImportSubmit = useCallback(async () => {
        if (!importData) return;

        setImporting(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/import`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: importData.name,
                        description: importFormData.description,
                        scriptType: importData.scriptType,
                        tags: importFormData.tags,
                        content: importData.content
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                handleCloseImportModal();
                fetchScripts();
                // Navigate to the newly imported script
                if (data.scriptId) {
                    navigate(`/admin/scripts/${data.scriptId}`);
                }
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to import script');
            }
        } catch (err) {
            console.error('Failed to import script:', err);
            setError('Failed to import script');
        } finally {
            setImporting(false);
        }
    }, [importData, importFormData, handleCloseImportModal, fetchScripts, navigate]);

    // Open delete modal
    const handleOpenDeleteModal = useCallback(async (script) => {
        setScriptToDelete(script);
        setDeleteConfirmName('');
        setShowDeleteModal(true);
        setLoadingDeleteLinks(true);
        setDeleteLinkedTasks([]);

        // Fetch linked tasks for this script
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${script.scriptId}/links`
            );
            if (response.ok) {
                const data = await response.json();
                setDeleteLinkedTasks(data.links ? data.links : []);
            }
        } catch (err) {
            console.error('Failed to fetch linked tasks for delete:', err);
        } finally {
            setLoadingDeleteLinks(false);
        }
    }, []);

    // Close delete modal
    const handleCloseDeleteModal = useCallback(() => {
        setShowDeleteModal(false);
        setScriptToDelete(null);
        setDeleteConfirmName('');
        setDeleteLinkedTasks([]);
    }, []);

    // Handle delete submission
    const handleDeleteSubmit = useCallback(async () => {
        if (!scriptToDelete) return;

        setDeleting(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${scriptToDelete.scriptId}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                handleCloseDeleteModal();
                fetchScripts();
                // If the deleted script was selected, clear selection
                if (selectedScript && selectedScript.scriptId === scriptToDelete.scriptId) {
                    navigate('/admin/scripts');
                }
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to delete script');
            }
        } catch (err) {
            console.error('Failed to delete script:', err);
            setError('Failed to delete script');
        } finally {
            setDeleting(false);
        }
    }, [scriptToDelete, handleCloseDeleteModal, fetchScripts, selectedScript, navigate]);

    // Open create script modal
    const handleOpenCreateModal = useCallback(() => {
        setShowCreateModal(true);
        setCreateFormData({ name: '', description: '', scriptType: 'powershell', tags: [] });
        setCreateNewTag('');
    }, []);

    // Close create script modal
    const handleCloseCreateModal = useCallback(() => {
        setShowCreateModal(false);
        setCreateFormData({ name: '', description: '', scriptType: 'powershell', tags: [] });
        setCreateNewTag('');
    }, []);

    // Handle adding a tag in create modal
    const handleAddCreateTag = useCallback(() => {
        const tag = createNewTag.trim().toLowerCase();
        if (tag && !createFormData.tags.includes(tag)) {
            setCreateFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
            setCreateNewTag('');
        }
    }, [createNewTag, createFormData.tags]);

    // Handle removing a tag in create modal
    const handleRemoveCreateTag = useCallback((tagToRemove) => {
        setCreateFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(t => t !== tagToRemove)
        }));
    }, []);

    // Handle create submission
    const handleCreateSubmit = useCallback(async () => {
        if (!createFormData.name.trim()) return;

        setCreating(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: createFormData.name.trim(),
                        description: createFormData.description.trim(),
                        scriptType: createFormData.scriptType,
                        tags: createFormData.tags
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                handleCloseCreateModal();
                fetchScripts();
                // Navigate to the newly created script
                if (data.scriptId) {
                    navigate(`/admin/scripts/${data.scriptId}`);
                }
            } else {
                const data = await response.json();
                setError(data.message ? data.message : 'Failed to create script');
            }
        } catch (err) {
            console.error('Failed to create script:', err);
            setError('Failed to create script');
        } finally {
            setCreating(false);
        }
    }, [createFormData, handleCloseCreateModal, fetchScripts, navigate]);

    // Initial load
    useEffect(() => {
        fetchScripts();
    }, [fetchScripts]);

    // Handle URL params - load script if scriptId in URL
    useEffect(() => {
        if (urlScriptId) {
            setScriptLinks([]);
            setWikiPages([]);
            fetchScript(urlScriptId);
            fetchVersions(urlScriptId);
            fetchScriptLinks(urlScriptId);
            fetchWikiPagesForScript(urlScriptId);
            const version = urlVersionNumber ? parseInt(urlVersionNumber, 10) : null;
            setSelectedVersion(version);
            fetchScriptContent(urlScriptId, version);
            // Reset diff viewer when script changes
            setShowDiffViewer(false);
            setCompareVersions({ v1: null, v2: null });
            // Reset rename state when script changes
            setIsRenaming(false);
            setRenameValue('');
            setRenameError('');
        } else {
            setSelectedScript(null);
            setSelectedVersion(null);
            setVersions([]);
            setScriptLinks([]);
            setWikiPages([]);
            setEditorContent('');
            setOriginalContent('');
            setHasUnsavedChanges(false);
            setIsEditing(false);
            setShowDiffViewer(false);
        }
    }, [urlScriptId, urlVersionNumber, fetchScript, fetchScriptContent, fetchScriptLinks, fetchWikiPagesForScript]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-12). TWO message types, because this page shows data
    // from two features:
    //
    //   `scripts_updated`   - the scripts table AND the script-task-links table
    //                         (D6: one type, because the reaction is the same)
    //   `tme_wiki_updated`  - the "Used by TME Wiki" panel, whose data lives on
    //                         the TME WIKI LINKS table, so it rides that
    //                         table's signal and not this page's own
    //
    // FOUR refetches: the list, and for the selected script its links, its
    // VERSIONS - without which a version saved elsewhere leaves the history
    // stale - and its wiki pages.
    // -----------------------------------------------------------------------

    // ⛔ GATE 3 IS A DATA-LOSS GATE HERE, AND IT IS SPLIT IN TWO.
    //
    // The in-flight flags below hold the WHOLE refresh, deferred (D11).
    // `importing` matters most: a bulk import creates many scripts, each one
    // streaming back to the very page doing the importing.
    //
    // `hasUnsavedChanges` is different, and it does NOT gate the handler: the
    // script list refreshes whether or not the editor is dirty (D3), and the
    // compare must run precisely WHEN it is dirty, because the compare is what
    // decides whether to raise the banner. A bare early return at the top would
    // make the banner - which exists for the dirty case - unreachable.
    const liveRefreshBlocked = Boolean(
        saving || rollingBack || linking || importing || deleting || savingRename || creating
    );
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => { liveRefreshBlockedRef.current = liveRefreshBlocked; }, [liveRefreshBlocked]);

    // "The script changed somewhere else" - D3. Cleared on reload, on save and
    // when the editor goes clean.
    const [staleScript, setStaleScript] = useState(false);
    useEffect(() => {
        if (!hasUnsavedChanges) setStaleScript(false);
    }, [hasUnsavedChanges]);

    // Read inside the handler, which must not re-subscribe on every keystroke.
    const liveStateRef = useRef({});
    liveStateRef.current = { selectedScript, selectedVersion, hasUnsavedChanges };

    // ⚠️ NO PAGE DEBOUNCE HERE, and that is deliberate (§13.2b). Five surfaces
    // debounce because their refetches are a scan or a Cognito call per row;
    // this one refetches KEYED Queries on a signal scoped to one script. What a
    // burst needs is coalescing, and the pending flags below give it that for
    // the case that matters - a bulk import, which sets `importing` and holds
    // every message until it finishes.
    const pendingRef = useRef({ scripts: false, wikiPages: false });

    const runLiveRefresh = useCallback(async () => {
        // GATE 0 lives HERE - at the point of refetch - not per message in the
        // socket handler (Fable, round 2). This page registers NO beforeunload,
        // so a background request taking apiAdmin's 401 path would discard a
        // dirty Monaco editor by a navigation the user did not cause; the
        // deferred flush reached that path with no gate at all. Checked BEFORE
        // the pending flags are taken, so a session that cannot be renewed
        // leaves them held for the next signal rather than dropping them (D11).
        if (!await adminSessionUsable()) return;
        const pending = pendingRef.current;
        pendingRef.current = { scripts: false, wikiPages: false };
        const { selectedScript: script, selectedVersion: version, hasUnsavedChanges: dirty }
            = liveStateRef.current;

        if (pending.wikiPages && script) {
            fetchWikiPagesForScript(script.scriptId, { silent: true });
        }
        if (!pending.scripts) return;

        // The list, always.
        fetchScripts({ silent: true });
        if (!script) return;

        fetchScriptLinks(script.scriptId, { silent: true });
        fetchVersions(script.scriptId, { silent: true });

        if (!dirty) {
            // Clean editor: silent refresh, exactly as everywhere else (D3).
            // ⛔ `fetchScript` SILENT TOO. Its loading flag swaps the whole right
            // panel for a spinner, so the non-silent call remounted Monaco -
            // cursor, scroll and undo history gone - a second after every save,
            // when the echo arrived (Fable, round 2).
            fetchScript(script.scriptId, { silent: true });
            fetchScriptContent(script.scriptId, version, { silent: true });
            return;
        }

        // ⛔ DIRTY: the typed text is NEVER touched. Instead, ask what the
        // server holds and compare it with the version we hold.
        //
        // ⚠️ "Equal" means "no change this editor needs to know about", NOT
        // "our own write" - a metadata-only edit by another admin (a rename, a
        // description) also leaves currentVersion equal and raises no banner.
        // That is correct: the banner protects the editor BODY, and the list
        // refreshed above regardless.
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${script.scriptId}`);
            if (!response.ok) return;
            const data = await response.json();
            if ((data.currentVersion || 0) > (script.currentVersion || 0)) {
                setStaleScript(true);
            }
        } catch (err) {
            console.error('Failed to check for a newer script version:', err);
        }
    }, [fetchScripts, fetchScript, fetchScriptContent, fetchScriptLinks, fetchVersions,
        fetchWikiPagesForScript]);

    // D11 - the deferred flush. Everything held while an action was in flight
    // runs ONCE when it finishes, however many messages arrived.
    useEffect(() => {
        if (liveRefreshBlocked) return;
        if (!pendingRef.current.scripts && !pendingRef.current.wikiPages) return;
        runLiveRefresh();
    }, [liveRefreshBlocked, runLiveRefresh]);

    useAdminWebSocket(useCallback((message) => {
        // GATE 0 is inside runLiveRefresh, at the point of refetch.
        // GATE 1 - type.
        if (message.type === 'scripts_updated') {
            // GATE 2 is applied per consumer inside runLiveRefresh: the LIST
            // wants every script, the editor and its panels want the selected
            // one.
            pendingRef.current.scripts = true;
        } else if (message.type === 'tme_wiki_updated') {
            if (!liveStateRef.current.selectedScript) return;
            pendingRef.current.wikiPages = true;
        } else {
            return;
        }

        // GATE 3 - hold it; the flush effect runs it when the action finishes.
        if (liveRefreshBlockedRef.current) return;
        runLiveRefresh();
    }, [runLiveRefresh]));

    /** D3's Reload: discard local edits and take the server's copy. */
    const handleStaleReload = useCallback(() => {
        const { selectedScript: script, selectedVersion: version } = liveStateRef.current;
        if (!script) return;
        setStaleScript(false);
        fetchScript(script.scriptId);
        fetchVersions(script.scriptId, { silent: true });
        fetchScriptContent(script.scriptId, version);
    }, [fetchScript, fetchVersions, fetchScriptContent]);

    // Handle script selection
    const handleSelectScript = (script) => {
        // Warn about unsaved changes
        if (hasUnsavedChanges) {
            const confirmLeave = window.confirm(UNSAVED_CHANGES_PROMPT);
            if (!confirmLeave) return;
        }
        navigate(`/admin/scripts/${script.scriptId}`);
    };

    // Rename handlers
    const handleRenameStart = useCallback(() => {
        setIsRenaming(true);
        setRenameValue(selectedScript.name);
        setRenameError('');
    }, [selectedScript]);

    const handleRenameCancel = useCallback(() => {
        if (savingRename) return;
        setIsRenaming(false);
        setRenameValue('');
        setRenameError('');
    }, [savingRename]);

    const handleRenameSave = useCallback(async () => {
        const trimmed = renameValue.trim();

        if (!trimmed) {
            setRenameError('Name cannot be empty');
            return;
        }
        if (trimmed.length > 100) {
            setRenameError('Name must be 100 characters or fewer');
            return;
        }
        if (trimmed === selectedScript.name) {
            handleRenameCancel();
            return;
        }

        setSavingRename(true);
        setRenameError('');
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${selectedScript.scriptId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: trimmed })
                }
            );

            if (response.ok) {
                const updated = await response.json();
                setSelectedScript(updated);
                setScripts(prev => prev.map(s =>
                    s.scriptId === updated.scriptId ? { ...s, name: updated.name } : s
                ));
                setIsRenaming(false);
                setRenameValue('');
            } else {
                const data = await response.json();
                setRenameError(data.message);
            }
        } catch (err) {
            console.error('Failed to rename script:', err);
            setRenameError('Failed to rename script');
        } finally {
            setSavingRename(false);
        }
    }, [renameValue, selectedScript, handleRenameCancel]);

    // Handle editor content change
    const handleEditorChange = (value) => {
        setEditorContent(value ? value : '');
        setHasUnsavedChanges(value !== originalContent);
    };

    // Handle editor mount
    const handleEditorMount = (editor) => {
        editorRef.current = editor;
    };

    // Handle save - opens modal to get version notes
    const handleSaveClick = () => {
        if (!hasUnsavedChanges && selectedScript?.currentVersion > 0) {
            return; // Nothing to save
        }
        setVersionNotes('');
        setShowSaveModal(true);
    };

    // Handle save confirm with version notes
    const handleSaveConfirm = async () => {
        if (!versionNotes.trim()) {
            return; // Version notes required
        }

        setSaving(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/scripts/${selectedScript.scriptId}/versions`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        versionNotes: versionNotes.trim(),
                        content: editorContent
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                // Update original content to match saved content
                setOriginalContent(editorContent);
                setHasUnsavedChanges(false);
                setShowSaveModal(false);
                setVersionNotes('');
                // Refresh script to get updated currentVersion
                fetchScript(selectedScript.scriptId);
                // Refresh scripts list to update version numbers
                fetchScripts();
            } else {
                const data = await response.json();
                setError(data.error ? data.error : 'Failed to save version');
            }
        } catch (err) {
            console.error('Failed to save version:', err);
            setError('Failed to save version');
        } finally {
            setSaving(false);
        }
    };

    // Handle cancel save modal
    const handleSaveCancel = () => {
        setShowSaveModal(false);
        setVersionNotes('');
    };

    // Get Monaco language based on script type
    const getMonacoLanguage = (scriptType) => {
        if (!scriptType) return 'plaintext';
        return scriptType === 'powershell' ? 'powershell' : 'shell';
    };

    // Get unique filter options from scripts
    const filterOptions = useMemo(() => {
        const options = {
            scriptType: new Set(),
            author: new Set(),
            tags: new Set()
        };

        scripts.forEach(script => {
            if (script.scriptType) options.scriptType.add(script.scriptType);
            if (script.author) options.author.add(script.author);
            if (script.tags && Array.isArray(script.tags)) {
                script.tags.forEach(tag => options.tags.add(tag));
            }
        });

        return {
            scriptType: Array.from(options.scriptType).sort(),
            author: Array.from(options.author).sort(),
            tags: Array.from(options.tags).sort()
        };
    }, [scripts]);

    // Filter scripts based on search, filters, and unlinked state
    const filteredScripts = useMemo(() => {
        // LOCAL safety check - create default filters if undefined
        const safeFilters = filters || { scriptType: new Set(), author: new Set(), tags: new Set() };
        return scripts.filter(script => {
            // Search filter
            if (searchTerm.trim()) {
                const search = searchTerm.toLowerCase();
                const matchesSearch = (
                    script.name.toLowerCase().includes(search) ||
                    (script.description && script.description.toLowerCase().includes(search)) ||
                    (script.tags && script.tags.some(tag => tag.toLowerCase().includes(search)))
                );
                if (!matchesSearch) return false;
            }

            // Script type filter - use safeFilters with optional chaining
            const scriptTypeFilter = safeFilters.scriptType || new Set();
            if (scriptTypeFilter.size > 0 && !scriptTypeFilter.has(script.scriptType)) {
                return false;
            }

            // Author filter - use safeFilters with optional chaining
            const authorFilter = safeFilters.author || new Set();
            if (authorFilter.size > 0 && !authorFilter.has(script.author)) {
                return false;
            }

            // Tags filter - match if script has any of the selected tags
            const tagsFilter = safeFilters.tags || new Set();
            if (tagsFilter.size > 0) {
                const scriptTags = Array.isArray(script.tags) ? script.tags : [];
                const hasMatchingTag = scriptTags.some(tag => tagsFilter.has(tag));
                if (!hasMatchingTag) return false;
            }

            // Unlinked filter
            if (showUnlinked && script.linkCount > 0) {
                return false;
            }

            return true;
        });
    }, [scripts, searchTerm, filters, showUnlinked]);

    // Sort filtered scripts
    const sortedScripts = useMemo(() => {
        return [...filteredScripts].sort((a, b) => {
            let aVal, bVal;

            switch (sortField) {
                case 'name':
                    aVal = (typeof a.name === 'string' ? a.name : '').toLowerCase();
                    bVal = (typeof b.name === 'string' ? b.name : '').toLowerCase();
                    break;
                case 'scriptType':
                    aVal = typeof a.scriptType === 'string' ? a.scriptType : '';
                    bVal = typeof b.scriptType === 'string' ? b.scriptType : '';
                    break;
                case 'author':
                    aVal = (typeof a.author === 'string' ? a.author : '').toLowerCase();
                    bVal = (typeof b.author === 'string' ? b.author : '').toLowerCase();
                    break;
                case 'currentVersion':
                    aVal = typeof a.currentVersion === 'number' ? a.currentVersion : 0;
                    bVal = typeof b.currentVersion === 'number' ? b.currentVersion : 0;
                    break;
                case 'updatedAt':
                default:
                    aVal = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    bVal = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    break;
            }

            if (typeof aVal === 'string') {
                return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredScripts, sortField, sortDir]);

    // Filter toggle handlers
    const toggleFilterValue = (column, value) => {
        setFilters(prev => {
            const base = prev ? prev : createDefaultFilters();
            const newSet = new Set(base[column]);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return { ...base, [column]: newSet };
        });
    };

    const clearFilter = (column) => {
        setFilters(prev => {
            const base = prev ? prev : createDefaultFilters();
            return { ...base, [column]: new Set() };
        });
    };

    const selectAllFilter = (column) => {
        setFilters(prev => {
            const base = prev ? prev : createDefaultFilters();
            return { ...base, [column]: new Set(filterOptions[column]) };
        });
    };

    const clearAllFilters = () => {
        setFilters(createDefaultFilters());
        setShowUnlinked(false);
    };

    // Sort toggle handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    // Check if any filters are active - LOCAL safety with inline default
    const safeFiltersForActive = filters || { scriptType: new Set(), author: new Set(), tags: new Set() };
    const hasActiveFilters = (
        ((safeFiltersForActive.scriptType || new Set()).size > 0) ||
        ((safeFiltersForActive.author || new Set()).size > 0) ||
        ((safeFiltersForActive.tags || new Set()).size > 0) ||
        showUnlinked
    );

    // Render filter dropdown
    const renderFilterDropdown = (column, label) => {
        // LOCAL safety - create default filters if undefined
        const safeFilters = filters || { scriptType: new Set(), author: new Set(), tags: new Set() };
        const columnFilter = safeFilters[column] || new Set();
        const isOpen = openFilter === column;
        const hasActiveFilter = columnFilter.size > 0;
        const options = filterOptions[column];

        if (options.length === 0) return null;

        return (
            <div className="filter-dropdown relative inline-flex items-center">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenFilter(isOpen ? null : column);
                    }}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                        hasActiveFilter
                            ? 'bg-primary-50 border-primary-300 text-primary-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    title={`Filter by ${label}`}
                >
                    <FaFilter className="w-2.5 h-2.5" />
                    <span>{label}</span>
                    {hasActiveFilter && (
                        <span className="bg-primary-600 text-white px-1 rounded-full text-[10px]">
                            {columnFilter.size}
                        </span>
                    )}
                </button>
                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[180px] max-h-[250px] overflow-auto">
                        <div className="p-2 border-b border-gray-200 flex gap-2">
                            <button
                                onClick={() => selectAllFilter(column)}
                                className="text-xs text-primary-600 hover:underline"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => clearFilter(column)}
                                className="text-xs text-primary-600 hover:underline"
                            >
                                Clear
                            </button>
                        </div>
                        <div className="p-1">
                            {options.map(option => (
                                <label
                                    key={option}
                                    className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 cursor-pointer rounded"
                                >
                                    <input
                                        type="checkbox"
                                        checked={columnFilter.has(option)}
                                        onChange={() => toggleFilterValue(column, option)}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {column === 'scriptType'
                                            ? (option === 'powershell' ? 'PowerShell' : 'BASH')
                                            : option}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50">
                <FaSpinner className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full flex bg-gray-50">
            {/* Left Panel - Script List */}
            <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                {/* Header with Create and Import Buttons */}
                <div className="p-3 border-b border-gray-200 space-y-2">
                    <button
                        onClick={handleOpenCreateModal}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                    >
                        <FaPlus className="w-3 h-3" />
                        New Script
                    </button>
                    <button
                        onClick={handleOpenImportModal}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                    >
                        <FaFileImport className="w-3 h-3" />
                        Import from File
                    </button>
                </div>

                {/* Search */}
                <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search scripts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters Bar */}
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-wrap gap-1.5">
                        {renderFilterDropdown('scriptType', 'Type')}
                        {renderFilterDropdown('author', 'Author')}
                        {renderFilterDropdown('tags', 'Tags')}

                        {/* Unlinked Toggle */}
                        <button
                            onClick={() => setShowUnlinked(!showUnlinked)}
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                                showUnlinked
                                    ? 'bg-orange-50 border-orange-300 text-orange-700'
                                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Show only unlinked scripts"
                        >
                            <FaUnlink className="w-2.5 h-2.5" />
                            <span>Unlinked</span>
                        </button>
                    </div>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            className="mt-1.5 text-xs text-primary-600 hover:underline"
                        >
                            Clear all filters
                        </button>
                    )}
                </div>

                {/* Sort Controls */}
                <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                        {sortedScripts.length} script{sortedScripts.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-1">
                        <select
                            value={sortField}
                            onChange={(e) => setSortField(e.target.value)}
                            className="text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                            <option value="updatedAt">Updated</option>
                            <option value="name">Name</option>
                            <option value="scriptType">Type</option>
                            <option value="author">Author</option>
                            <option value="currentVersion">Version</option>
                        </select>
                        <button
                            onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            {sortDir === 'asc' ? (
                                <FaSortAmountUp className="w-3 h-3" />
                            ) : (
                                <FaSortAmountDown className="w-3 h-3" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Script List */}
                <div className="flex-1 overflow-y-auto p-2">
                    {sortedScripts.length === 0 ? (
                        <div className="text-center py-8">
                            <FaCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">
                                {searchTerm || hasActiveFilters ? 'No matching scripts' : 'No scripts yet'}
                            </p>
                            {!searchTerm && !hasActiveFilters && (
                                <p className="text-xs text-gray-400 mt-1">
                                    Create your first script to get started
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {sortedScripts.map(script => (
                                <div
                                    key={script.scriptId}
                                    className={`
                                        group flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer relative
                                        ${(selectedScript && selectedScript.scriptId) === script.scriptId
                                            ? 'bg-primary-50 border border-primary-200'
                                            : 'hover:bg-gray-100'}
                                    `}
                                    onClick={() => handleSelectScript(script)}
                                >
                                    <div className="flex-shrink-0 mt-0.5">
                                        {script.scriptType === 'powershell' ? (
                                            <FaTerminal className="w-4 h-4 text-blue-500" />
                                        ) : (
                                            <FaFileAlt className="w-4 h-4 text-green-600" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-700 truncate">
                                                {script.name}
                                            </span>
                                            {/* Unlinked Indicator */}
                                            {script.linkCount === 0 && (
                                                <span
                                                    className="flex-shrink-0"
                                                    title="Not linked to any tasks"
                                                >
                                                    <FaUnlink className="w-3 h-3 text-orange-400" />
                                                </span>
                                            )}
                                        </div>
                                        {script.description && (
                                            <p className="text-xs text-gray-500 truncate mt-0.5">
                                                {script.description}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-gray-400">
                                                v{script.currentVersion}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                {script.scriptType === 'powershell' ? '.ps1' : '.sh'}
                                            </span>
                                            {script.linkCount > 0 && (
                                                <span className="text-xs text-gray-400">
                                                    {script.linkCount} link{script.linkCount !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Delete button - appears on hover */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDeleteModal(script);
                                        }}
                                        className="absolute right-2 top-2 p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 text-red-500 transition-opacity"
                                        title="Delete script"
                                    >
                                        <FaTrash className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right Panel - Editor */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
                {loadingScript ? (
                    <div className="flex-1 flex items-center justify-center">
                        <FaSpinner className="w-6 h-6 text-primary-500 animate-spin" />
                    </div>
                ) : (selectedScript && selectedScript.scriptType) ? (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Script Header */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                {selectedScript.scriptType === 'powershell' ? (
                                    <FaTerminal className="w-5 h-5 text-blue-500" />
                                ) : (
                                    <FaFileAlt className="w-5 h-5 text-green-600" />
                                )}
                                <div>
                                    {isRenaming ? (
                                        <input
                                            type="text"
                                            value={renameValue}
                                            onChange={(e) => {
                                                if (e.target.value.length <= 100) {
                                                    setRenameValue(e.target.value);
                                                    setRenameError('');
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleRenameSave();
                                                } else if (e.key === 'Escape') {
                                                    handleRenameCancel();
                                                }
                                            }}
                                            onBlur={handleRenameCancel}
                                            autoFocus
                                            className="font-semibold text-gray-900 w-full border-b-2 border-gray-300 focus:border-primary-600 focus:outline-none pb-1 bg-transparent"
                                            disabled={savingRename}
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <h2 className="font-semibold text-gray-900">{selectedScript.name}</h2>
                                            <button
                                                onClick={handleRenameStart}
                                                className="text-gray-400 hover:text-gray-600"
                                                title="Rename script"
                                            >
                                                <FaPencilAlt className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                    {renameError && (
                                        <p className="text-xs text-red-500 mt-1">{renameError}</p>
                                    )}
                                    {selectedScript.description && (
                                        <p className="text-sm text-gray-500">{selectedScript.description}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span>Version {selectedScript.currentVersion}</span>
                                <span>By {selectedScript.author}</span>
                                <span>
                                    Updated {new Date(selectedScript.updatedAt).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </span>
                            </div>
                            {/* Current version notes */}
                            {(() => {
                                const currentVersionData = versions.find(v => v.versionNumber === selectedScript.currentVersion);
                                if (currentVersionData && currentVersionData.versionNotes) {
                                    return (
                                        <div className="mt-2 text-sm text-gray-600">
                                            Version Notes: {currentVersionData.versionNotes}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        {/* Editor Toolbar */}
                        <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {selectedVersion ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FaEye className="w-4 h-4" />
                                        <span>Viewing version {selectedVersion} (read-only)</span>
                                        <button
                                            onClick={() => navigate(`/admin/scripts/${selectedScript.scriptId}`)}
                                            className="ml-2 px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200"
                                        >
                                            Back to current
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        {isEditing ? (
                                            <>
                                                <FaEdit className="w-4 h-4 text-primary-600" />
                                                <span>Editing</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaEye className="w-4 h-4" />
                                                <span>Viewing</span>
                                            </>
                                        )}
                                        {hasUnsavedChanges && (
                                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                                                Unsaved changes
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!selectedVersion && isEditing && (
                                    <button
                                        onClick={handleSaveClick}
                                        disabled={!hasUnsavedChanges && selectedScript?.currentVersion > 0}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium ${
                                            hasUnsavedChanges
                                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                : selectedScript?.currentVersion === 0
                                                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        <FaSave className="w-3 h-3" />
                                        Save Version
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Editor and Version History Container */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* D3: somebody else saved this script while it was
                                open and dirty. The typed text stays exactly as
                                it is; this is the only way to discard it. */}
                            {staleScript && (
                                <div className="px-4 pt-3">
                                    <StaleContentBanner
                                        message="This script was saved somewhere else. Your changes are still here."
                                        onReload={handleStaleReload}
                                        confirm={UNSAVED_CHANGES_PROMPT}
                                        busy={saving}
                                    />
                                </div>
                            )}
                            {/* Main Content Area - Editor or Diff Viewer */}
                            <div className={`flex-1 overflow-hidden ${showDiffViewer ? 'flex' : ''}`}>
                                {showDiffViewer ? (
                                    /* Diff Viewer */
                                    <div className="flex-1 flex flex-col overflow-hidden">
                                        {/* Diff Toolbar */}
                                        <div className="px-4 py-2 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm">
                                                <FaExchangeAlt className="w-4 h-4 text-blue-600" />
                                                <span className="text-blue-800">
                                                    Comparing version {compareVersions.v1} with version {compareVersions.v2}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setDiffSideBySide(!diffSideBySide)}
                                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                                                    title={diffSideBySide ? 'Switch to inline view' : 'Switch to side-by-side view'}
                                                >
                                                    <FaColumns className="w-3 h-3" />
                                                    {diffSideBySide ? 'Inline' : 'Side-by-side'}
                                                </button>
                                                <button
                                                    onClick={handleCloseDiffViewer}
                                                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
                                                >
                                                    Close diff
                                                </button>
                                            </div>
                                        </div>
                                        {/* Diff Editor */}
                                        <div className="flex-1 overflow-hidden">
                                            {loadingCompare ? (
                                                <div className="h-full flex items-center justify-center bg-gray-50">
                                                    <div className="text-center">
                                                        <FaSpinner className="w-6 h-6 text-primary-500 animate-spin mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500">Loading versions for comparison...</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <DiffEditor
                                                    height="100%"
                                                    language={getMonacoLanguage(selectedScript.scriptType)}
                                                    original={compareContent.original}
                                                    modified={compareContent.modified}
                                                    options={{
                                                        readOnly: true,
                                                        renderSideBySide: diffSideBySide,
                                                        minimap: { enabled: false },
                                                        lineNumbers: 'on',
                                                        scrollBeyondLastLine: false,
                                                        wordWrap: 'on',
                                                        automaticLayout: true,
                                                        fontSize: 14
                                                    }}
                                                    loading={
                                                        <div className="h-full flex items-center justify-center bg-gray-50">
                                                            <div className="text-center">
                                                                <FaSpinner className="w-6 h-6 text-primary-500 animate-spin mx-auto mb-2" />
                                                                <p className="text-sm text-gray-500">Loading diff viewer...</p>
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Regular Editor */
                                    loadingContent ? (
                                        <div className="h-full flex items-center justify-center bg-gray-50">
                                            <FaSpinner className="w-6 h-6 text-primary-500 animate-spin" />
                                        </div>
                                    ) : (
                                        <Editor
                                            height="100%"
                                            language={getMonacoLanguage(selectedScript.scriptType)}
                                            value={editorContent}
                                            onChange={handleEditorChange}
                                            onMount={handleEditorMount}
                                            options={{
                                                readOnly: !!selectedVersion,
                                                minimap: { enabled: false },
                                                lineNumbers: 'on',
                                                scrollBeyondLastLine: false,
                                                wordWrap: 'on',
                                                automaticLayout: true,
                                                fontSize: 14,
                                                tabSize: 4,
                                                insertSpaces: true,
                                                folding: true,
                                                lineDecorationsWidth: 10,
                                                renderLineHighlight: 'line',
                                                selectOnLineNumbers: true,
                                                roundedSelection: false,
                                                cursorStyle: 'line',
                                                scrollbar: {
                                                    vertical: 'auto',
                                                    horizontal: 'auto'
                                                }
                                            }}
                                            loading={
                                                <div className="h-full flex items-center justify-center bg-gray-50">
                                                    <div className="text-center">
                                                        <FaSpinner className="w-6 h-6 text-primary-500 animate-spin mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500">Loading editor...</p>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    )
                                )}
                            </div>

                            {/* Task Links Panel */}
                            <div className="border-t border-gray-200 bg-gray-50">
                                {/* Task Links Header */}
                                <button
                                    onClick={() => setTaskLinksExpanded(!taskLinksExpanded)}
                                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100 transition-colours"
                                >
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                        {taskLinksExpanded ? (
                                            <FaChevronDown className="w-3 h-3" />
                                        ) : (
                                            <FaChevronRight className="w-3 h-3" />
                                        )}
                                        <FaLink className="w-4 h-4" />
                                        <span>Linked Tasks</span>
                                        <span className="text-xs text-gray-500">
                                            ({scriptLinks.length} task{scriptLinks.length !== 1 ? 's' : ''})
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenLinkModal();
                                        }}
                                        className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                                    >
                                        + Link to Task
                                    </button>
                                </button>

                                {/* Task Links Content */}
                                {taskLinksExpanded && (
                                    <div className="border-t border-gray-200">
                                        {loadingLinks ? (
                                            <div className="p-4 flex items-center justify-center">
                                                <FaSpinner className="w-4 h-4 text-primary-500 animate-spin" />
                                            </div>
                                        ) : scriptLinks.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                <p>This script is not linked to any tasks.</p>
                                                <button
                                                    onClick={handleOpenLinkModal}
                                                    className="mt-2 text-primary-600 hover:underline"
                                                >
                                                    Link to a task
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-200">
                                                {scriptLinks.map((link) => {
                                                    const taskName = getTaskLinkDisplay(link);
                                                    return (
                                                    <div
                                                        key={link.taskKey}
                                                        className="px-4 py-2 flex items-center justify-between text-sm hover:bg-gray-100"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-gray-700 truncate">
                                                                {taskName}
                                                                {link.taskType && (
                                                                    <span className="ml-2 text-xs font-normal text-gray-500">
                                                                        [{getTaskTypeLabel(link.taskType)}]
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                Linked {new Date(link.linkedAt).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric'
                                                                })}
                                                                {' by '}
                                                                {link.linkedBy}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleUnlinkFromTask(taskName)}
                                                            disabled={unlinking === taskName}
                                                            className="p-1.5 rounded hover:bg-red-100 text-red-500"
                                                            title="Unlink from task"
                                                        >
                                                            {unlinking === taskName ? (
                                                                <FaSpinner className="w-3 h-3 animate-spin" />
                                                            ) : (
                                                                <FaUnlink className="w-3 h-3" />
                                                            )}
                                                        </button>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Used by TME Wiki (TWK-24, §9.6) - READ ONLY.
                                ⚠️ AdminScriptsFunction is granted nothing on the
                                internal wiki's tables; the browser calls the TME
                                Wiki route with the same admin token. A page
                                links itself to a script, from the page. */}
                            <div className="border-t border-gray-200 bg-gray-50">
                                <button
                                    onClick={() => setWikiPagesExpanded(!wikiPagesExpanded)}
                                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100 transition-colours"
                                >
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                        {wikiPagesExpanded ? (
                                            <FaChevronDown className="w-3 h-3" />
                                        ) : (
                                            <FaChevronRight className="w-3 h-3" />
                                        )}
                                        <FaBook className="w-4 h-4" />
                                        <span>Used by TME Wiki</span>
                                        <span className="text-xs text-gray-500">
                                            ({wikiPages.length} page{wikiPages.length !== 1 ? 's' : ''})
                                        </span>
                                    </div>
                                </button>

                                {wikiPagesExpanded && (
                                    <div className="border-t border-gray-200 max-h-48 overflow-y-auto">
                                        {loadingWikiPages ? (
                                            <div className="p-4 flex items-center justify-center">
                                                <FaSpinner className="w-4 h-4 text-primary-500 animate-spin" />
                                            </div>
                                        ) : wikiPages.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                No TME Wiki page references this script.
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-200">
                                                {wikiPages.map((link) => (
                                                    <button
                                                        key={`${link.wikiId}#${link.pageId}`}
                                                        onClick={() => openWikiPage(link)}
                                                        className="w-full px-4 py-2 flex items-center justify-between text-sm text-left hover:bg-gray-100"
                                                    >
                                                        <span className="font-medium text-gray-700 truncate">
                                                            {link.pageTitle}
                                                        </span>
                                                        <FaExternalLinkAlt className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Version History Panel */}
                            <div className="border-t border-gray-200 bg-gray-50">
                                {/* Version History Header */}
                                <button
                                    onClick={() => setVersionHistoryExpanded(!versionHistoryExpanded)}
                                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100 transition-colours"
                                >
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                        {versionHistoryExpanded ? (
                                            <FaChevronDown className="w-3 h-3" />
                                        ) : (
                                            <FaChevronRight className="w-3 h-3" />
                                        )}
                                        <FaHistory className="w-4 h-4" />
                                        <span>Version History</span>
                                        {versions.length > 0 && (
                                            <span className="text-xs text-gray-500">
                                                ({versions.length} version{versions.length !== 1 ? 's' : ''})
                                            </span>
                                        )}
                                    </div>
                                </button>

                                {/* Version History Content */}
                                {versionHistoryExpanded && (
                                    <div className="border-t border-gray-200 max-h-48 overflow-y-auto">
                                        {loadingVersions ? (
                                            <div className="p-4 flex items-center justify-center">
                                                <FaSpinner className="w-4 h-4 text-primary-500 animate-spin" />
                                            </div>
                                        ) : versions.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                No versions yet. Save the script to create the first version.
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-200">
                                                {versions.map((version, index) => {
                                                    const isCurrent = version.versionNumber === selectedScript.currentVersion;
                                                    const isViewing = selectedVersion === version.versionNumber;
                                                    return (
                                                        <div
                                                            key={version.versionNumber}
                                                            className={`px-4 py-2 flex items-center justify-between text-sm ${
                                                                isViewing ? 'bg-primary-50' : 'hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`font-medium ${isCurrent ? 'text-primary-700' : 'text-gray-700'}`}>
                                                                        v{version.versionNumber}
                                                                    </span>
                                                                    {isCurrent && (
                                                                        <span className="px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
                                                                            Current
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                                                    {version.versionNotes}
                                                                </p>
                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                    {new Date(version.createdAt).toLocaleDateString('en-GB', {
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                    {' by '}
                                                                    {version.createdBy}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-1 ml-2">
                                                                {/* View version */}
                                                                <button
                                                                    onClick={() => navigate(`/admin/scripts/${selectedScript.scriptId}/version/${version.versionNumber}`)}
                                                                    className={`p-1.5 rounded ${
                                                                        isViewing
                                                                            ? 'bg-primary-200 text-primary-700'
                                                                            : 'hover:bg-gray-200 text-gray-500'
                                                                    }`}
                                                                    title="View this version"
                                                                >
                                                                    <FaEye className="w-3 h-3" />
                                                                </button>
                                                                {/* Compare with next version */}
                                                                {index < versions.length - 1 && (
                                                                    <button
                                                                        onClick={() => handleCompareVersions(
                                                                            versions[index + 1].versionNumber,
                                                                            version.versionNumber
                                                                        )}
                                                                        className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                                                                        title={`Compare v${versions[index + 1].versionNumber} with v${version.versionNumber}`}
                                                                    >
                                                                        <FaExchangeAlt className="w-3 h-3" />
                                                                    </button>
                                                                )}
                                                                {/* Rollback (not for current version) */}
                                                                {!isCurrent && (
                                                                    <button
                                                                        onClick={() => handleRollback(version.versionNumber)}
                                                                        disabled={rollingBack}
                                                                        className="p-1.5 rounded hover:bg-orange-100 text-orange-500"
                                                                        title={`Rollback to v${version.versionNumber}`}
                                                                    >
                                                                        {rollingBack ? (
                                                                            <FaSpinner className="w-3 h-3 animate-spin" />
                                                                        ) : (
                                                                            <FaUndo className="w-3 h-3" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <FaCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600">Select a Script</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                Choose a script from the list or create a new one.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Error Toast */}
            {error && (
                <div className="fixed bottom-4 right-4 bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
                    <FaExclamationTriangle className="w-5 h-5" />
                    <span className="text-sm">{error}</span>
                    <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Save Version Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Save New Version
                            </h3>
                        </div>
                        <div className="px-6 py-4">
                            <p className="text-sm text-gray-600 mb-4">
                                This will create version {(selectedScript?.currentVersion ? selectedScript.currentVersion : 0) + 1} of this script.
                            </p>
                            <label className="block mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                    Version Notes <span className="text-red-500">*</span>
                                </span>
                                <textarea
                                    value={versionNotes}
                                    onChange={(e) => setVersionNotes(e.target.value)}
                                    placeholder="Describe what changed in this version..."
                                    rows={3}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    autoFocus
                                />
                            </label>
                            <p className="text-xs text-gray-500">
                                Version notes are required and help track changes over time.
                            </p>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={handleSaveCancel}
                                disabled={saving}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveConfirm}
                                disabled={saving || !versionNotes.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="w-4 h-4" />
                                        Save Version
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Link to Task Modal - Two-Panel Transfer List UI (v2) */}
            {showLinkModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Link Script to Tasks
                            </h3>
                            <button
                                onClick={() => setShowLinkModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-6 py-4 flex-1 overflow-hidden flex flex-col">
                            <p className="text-sm text-gray-600 mb-4">
                                Drag tasks between panels to link or unlink. Scripts are linked by task NAME, so linking applies to all tasks with the same name across templates.
                            </p>

                            {/* Two-Panel Transfer List */}
                            <div className="flex-1 flex gap-4 min-h-0">
                                {/* Left Panel - Available Tasks */}
                                <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Tasks</h4>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={taskSearchTerm}
                                                onChange={(e) => setTaskSearchTerm(e.target.value)}
                                                placeholder="Search..."
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            />
                                            <select
                                                value={taskTypeFilter}
                                                onChange={(e) => setTaskTypeFilter(e.target.value)}
                                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            >
                                                <option value="all">All</option>
                                                <option value="maintask">Main Tasks</option>
                                                <option value="subtask">Subtasks</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex-1 overflow-y-auto ${dragOverPanel === 'available' ? 'bg-red-50 ring-2 ring-red-300 ring-inset' : ''}`}
                                        onDragOver={(e) => handleDragOver(e, 'available')}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDropOnAvailable}
                                    >
                                        {loadingAvailableTasks ? (
                                            <div className="p-8 flex items-center justify-center">
                                                <FaSpinner className="w-6 h-6 text-primary-500 animate-spin" />
                                            </div>
                                        ) : filteredAvailableTasks.length === 0 ? (
                                            <div className="p-8 text-center text-sm text-gray-500">
                                                {availableTasks.length === 0
                                                    ? 'No tasks found in templates'
                                                    : taskSearchTerm
                                                        ? 'No matching tasks found'
                                                        : 'All available tasks are linked'}
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {filteredAvailableTasks.map((task) => (
                                                    <div
                                                        key={task.taskName}
                                                        draggable
                                                        onDragStart={(e) => handleDragStart(e, task, 'available')}
                                                        onDragEnd={handleDragEnd}
                                                        className="px-4 py-2 flex items-center gap-2 cursor-grab hover:bg-gray-50 active:cursor-grabbing"
                                                    >
                                                        <FaGripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-800 truncate">
                                                                {task.taskName}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {getTaskTypeLabel(task.taskType)}
                                                            </p>
                                                        </div>
                                                        {linking === task.taskName && (
                                                            <FaSpinner className="w-3 h-3 text-primary-500 animate-spin flex-shrink-0" />
                                                        )}
                                                        <FaArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Panel - Linked Tasks */}
                                <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                        <h4 className="text-sm font-semibold text-gray-700">
                                            Linked Tasks ({linkedTasksList.length})
                                        </h4>
                                        <p className="text-xs text-gray-500 mt-1">Drag here to link, or drag back to unlink</p>
                                    </div>
                                    <div
                                        className={`flex-1 overflow-y-auto ${dragOverPanel === 'linked' ? 'bg-green-50 ring-2 ring-green-300 ring-inset' : ''}`}
                                        onDragOver={(e) => handleDragOver(e, 'linked')}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDropOnLinked}
                                    >
                                        {linkedTasksList.length === 0 ? (
                                            <div className="p-8 text-center text-sm text-gray-500">
                                                No tasks linked yet. Drag tasks here to link them.
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {linkedTasksList.map((task) => (
                                                    <div
                                                        key={task.taskName}
                                                        draggable
                                                        onDragStart={(e) => handleDragStart(e, task, 'linked')}
                                                        onDragEnd={handleDragEnd}
                                                        className="px-4 py-2 flex items-center gap-2 cursor-grab hover:bg-gray-50 active:cursor-grabbing bg-green-50/50"
                                                    >
                                                        <FaArrowLeft className="w-3 h-3 text-gray-300 flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-800 truncate">
                                                                {task.taskName}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {getTaskTypeLabel(task.taskType)}
                                                            </p>
                                                        </div>
                                                        {unlinking === task.taskName && (
                                                            <FaSpinner className="w-3 h-3 text-primary-500 animate-spin flex-shrink-0" />
                                                        )}
                                                        <FaGripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={() => setShowLinkModal(false)}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Script Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Import Script from File
                            </h3>
                            <button
                                onClick={handleCloseImportModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            {/* File Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select File <span className="text-red-500">*</span>
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".ps1,.sh"
                                    onChange={handleImportFileChange}
                                    className="block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-lg file:border-0
                                        file:text-sm file:font-medium
                                        file:bg-primary-50 file:text-primary-700
                                        hover:file:bg-primary-100
                                        cursor-pointer"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Supported: .ps1 (PowerShell) and .sh (BASH)
                                </p>
                            </div>

                            {/* Show file details after selection */}
                            {importData && (
                                <>
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-2 mb-2">
                                            {importData.scriptType === 'powershell' ? (
                                                <FaTerminal className="w-4 h-4 text-blue-500" />
                                            ) : (
                                                <FaFileAlt className="w-4 h-4 text-green-600" />
                                            )}
                                            <span className="text-sm font-medium">{importData.filename}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Type: {importData.scriptType === 'powershell' ? 'PowerShell' : 'BASH'}
                                            {' | '}
                                            {importData.content.length} characters
                                        </div>
                                    </div>

                                    {/* Name (editable) */}
                                    <label className="block mb-4">
                                        <span className="text-sm font-medium text-gray-700">
                                            Name <span className="text-red-500">*</span>
                                        </span>
                                        <input
                                            type="text"
                                            value={importData.name}
                                            onChange={(e) => setImportData(prev => ({ ...prev, name: e.target.value }))}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </label>

                                    {/* Description */}
                                    <label className="block mb-4">
                                        <span className="text-sm font-medium text-gray-700">Description</span>
                                        <textarea
                                            value={importFormData.description}
                                            onChange={(e) => setImportFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Optional description for this script..."
                                            rows={2}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                    </label>

                                    {/* Tags */}
                                    <div className="mb-2">
                                        <span className="text-sm font-medium text-gray-700">Tags</span>
                                        <div className="mt-1 flex gap-2">
                                            <input
                                                type="text"
                                                value={newTag}
                                                onChange={(e) => setNewTag(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddImportTag();
                                                    }
                                                }}
                                                placeholder="Add a tag..."
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                            <button
                                                onClick={handleAddImportTag}
                                                disabled={!newTag.trim()}
                                                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {importFormData.tags.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {importFormData.tags.map(tag => (
                                                    <span
                                                        key={tag}
                                                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded"
                                                    >
                                                        <FaTag className="w-2.5 h-2.5" />
                                                        {tag}
                                                        <button
                                                            onClick={() => handleRemoveImportTag(tag)}
                                                            className="ml-1 text-primary-500 hover:text-primary-700"
                                                        >
                                                            <FaTimes className="w-2.5 h-2.5" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={handleCloseImportModal}
                                disabled={importing}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleImportSubmit}
                                disabled={importing || !importData || !importData.name.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {importing ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Importing...
                                    </>
                                ) : (
                                    <>
                                        <FaFileImport className="w-4 h-4" />
                                        Import Script
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Script Modal */}
            {showDeleteModal && scriptToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Delete Script
                            </h3>
                        </div>
                        <div className="px-6 py-4">
                            <div className="flex items-center gap-3 mb-4 p-3 bg-red-50 rounded-lg">
                                <FaExclamationTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-red-800">
                                        You are about to delete this script
                                    </p>
                                    <p className="text-xs text-red-600 mt-1">
                                        This action can be undone by restoring the script later.
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                    {scriptToDelete.scriptType === 'powershell' ? (
                                        <FaTerminal className="w-4 h-4 text-blue-500" />
                                    ) : (
                                        <FaFileAlt className="w-4 h-4 text-green-600" />
                                    )}
                                    <span className="font-medium text-gray-900">{scriptToDelete.name}</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Version {scriptToDelete.currentVersion}
                                    {' | '}
                                    {scriptToDelete.scriptType === 'powershell' ? 'PowerShell' : 'BASH'}
                                </p>
                            </div>

                            {/* Linked Tasks Warning */}
                            {loadingDeleteLinks ? (
                                <div className="mb-4 flex items-center justify-center py-2">
                                    <FaSpinner className="w-4 h-4 text-primary-500 animate-spin" />
                                </div>
                            ) : deleteLinkedTasks.length > 0 ? (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm font-medium text-yellow-800 mb-2">
                                        This script is linked to {deleteLinkedTasks.length} task{deleteLinkedTasks.length !== 1 ? 's' : ''}:
                                    </p>
                                    <ul className="text-xs text-yellow-700 max-h-24 overflow-y-auto">
                                        {deleteLinkedTasks.map(link => (
                                            <li key={link.taskKey} className="truncate py-0.5">
                                                {link.taskKey}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-xs text-yellow-600 mt-2">
                                        Links will be preserved and restored if the script is restored.
                                    </p>
                                </div>
                            ) : null}

                            {/* Confirmation Input */}
                            <div>
                                <p className="text-sm text-gray-600 mb-2">
                                    Type <strong>{scriptToDelete.name}</strong> to confirm deletion:
                                </p>
                                <input
                                    type="text"
                                    value={deleteConfirmName}
                                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                                    placeholder="Script name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={handleCloseDeleteModal}
                                disabled={deleting}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteSubmit}
                                disabled={deleting || deleteConfirmName !== scriptToDelete.name}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <FaTrash className="w-4 h-4" />
                                        Delete Script
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Script Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Create New Script
                            </h3>
                            <button
                                onClick={handleCloseCreateModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            {/* Name */}
                            <label className="block mb-4">
                                <span className="text-sm font-medium text-gray-700">
                                    Name <span className="text-red-500">*</span>
                                </span>
                                <input
                                    type="text"
                                    value={createFormData.name}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Script name"
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    autoFocus
                                />
                            </label>

                            {/* Script Type */}
                            <label className="block mb-4">
                                <span className="text-sm font-medium text-gray-700">
                                    Script Type <span className="text-red-500">*</span>
                                </span>
                                <select
                                    value={createFormData.scriptType}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, scriptType: e.target.value }))}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                    <option value="powershell">PowerShell (.ps1)</option>
                                    <option value="bash">BASH (.sh)</option>
                                </select>
                            </label>

                            {/* Description */}
                            <label className="block mb-4">
                                <span className="text-sm font-medium text-gray-700">Description</span>
                                <textarea
                                    value={createFormData.description}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Optional description for this script..."
                                    rows={2}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </label>

                            {/* Tags */}
                            <div className="mb-2">
                                <span className="text-sm font-medium text-gray-700">Tags</span>
                                <div className="mt-1 flex gap-2">
                                    <input
                                        type="text"
                                        value={createNewTag}
                                        onChange={(e) => setCreateNewTag(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddCreateTag();
                                            }
                                        }}
                                        placeholder="Add a tag..."
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                    <button
                                        onClick={handleAddCreateTag}
                                        disabled={!createNewTag.trim()}
                                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-50"
                                    >
                                        Add
                                    </button>
                                </div>
                                {createFormData.tags.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {createFormData.tags.map(tag => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded"
                                            >
                                                <FaTag className="w-2.5 h-2.5" />
                                                {tag}
                                                <button
                                                    onClick={() => handleRemoveCreateTag(tag)}
                                                    className="ml-1 text-primary-500 hover:text-primary-700"
                                                >
                                                    <FaTimes className="w-2.5 h-2.5" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={handleCloseCreateModal}
                                disabled={creating}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateSubmit}
                                disabled={creating || !createFormData.name.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {creating ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FaPlus className="w-4 h-4" />
                                        Create Script
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ScriptRepository;
